import { useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Spot } from '@/types/spots';

interface SpotsMapProps {
  spots: Spot[];
  selectedSpot: Spot | null;
  onSelectSpot: (spot: Spot) => void;
  userLocation: { lat: number; lng: number } | null;
}

const MAPBOX_TOKEN = 'pk.eyJ1IjoiemFpbmg0MiIsImEiOiJjbTR1NHBoYjMwZXQ4MmtzYWIwZzJzaWR6In0.gE5QluhHA_G_TzvGpOsRdg';

const SpotsMap = ({ spots, selectedSpot, onSelectSpot, userLocation }: SpotsMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const userMarkerRef = useRef<mapboxgl.Marker | null>(null);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
  }, []);

  // Initialize map with minimal style
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      // Use minimal monochrome style for cleaner look
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-0.1276, 51.5074],
      zoom: 12,
      // Disable rotation for simpler feel
      dragRotate: false,
      touchZoomRotate: false,
      // Smoother animations
      fadeDuration: 0,
    });

    // Apply minimal styling after load
    map.current.on('style.load', () => {
      // Mute colors for a cleaner, simpler look
      map.current?.setPaintProperty('land', 'background-color', '#f8f8f8');
    });

    // Remove compass, only keep zoom
    map.current.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }), 
      'top-right'
    );

    return () => {
      clearMarkers();
      map.current?.remove();
      map.current = null;
    };
  }, [clearMarkers]);

  // Update user location marker
  useEffect(() => {
    if (!map.current || !userLocation) return;

    if (userMarkerRef.current) {
      userMarkerRef.current.setLngLat([userLocation.lng, userLocation.lat]);
    } else {
      const el = document.createElement('div');
      el.style.width = '14px';
      el.style.height = '14px';
      el.style.backgroundColor = '#3b82f6';
      el.style.borderRadius = '50%';
      el.style.border = '3px solid white';
      el.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.3)';

      userMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([userLocation.lng, userLocation.lat])
        .addTo(map.current);
    }

    map.current.easeTo({
      center: [userLocation.lng, userLocation.lat],
      zoom: 14,
      duration: 800,
    });
  }, [userLocation]);

  // Update spot markers - rating badges like reference
  useEffect(() => {
    if (!map.current) return;

    clearMarkers();

    spots.forEach((spot) => {
      if (!spot.coordinates) return;

      const isSelected = selectedSpot?.id === spot.id;
      
      // Calculate a rating from amenities (average of wifi, quiet, seats)
      const rating = ((spot.wifiQuality + spot.quietLevel + spot.seatAvailability) / 3).toFixed(1);

      // Create rating badge element
      const el = document.createElement('div');
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.gap = '4px';
      el.style.backgroundColor = 'white';
      el.style.padding = '6px 10px';
      el.style.borderRadius = '20px';
      el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
      el.style.cursor = 'pointer';
      el.style.fontFamily = 'DM Sans, sans-serif';
      el.style.fontSize = '14px';
      el.style.fontWeight = '600';
      el.style.color = '#1f3d32';
      el.style.transition = 'transform 0.15s ease, box-shadow 0.15s ease';
      
      if (isSelected) {
        el.style.transform = 'scale(1.1)';
        el.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
      }

      el.innerHTML = `${rating} <span style="color: #1f3d32">★</span>`;

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onSelectSpot(spot);
      });
      
      el.addEventListener('mouseenter', () => {
        el.style.transform = 'scale(1.05)';
      });
      
      el.addEventListener('mouseleave', () => {
        el.style.transform = isSelected ? 'scale(1.1)' : 'scale(1)';
      });

      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([spot.coordinates.lng, spot.coordinates.lat])
        .addTo(map.current!);

      markersRef.current.push(marker);
    });
  }, [spots, selectedSpot, onSelectSpot, clearMarkers]);

  // Smooth pan to selected spot
  useEffect(() => {
    if (!map.current || !selectedSpot?.coordinates) return;

    map.current.easeTo({
      center: [selectedSpot.coordinates.lng, selectedSpot.coordinates.lat],
      zoom: 14,
      duration: 600,
    });
  }, [selectedSpot]);

  return (
    <div ref={mapContainer} className="w-full h-full" />
  );
};

export default SpotsMap;
